import React from 'react';

import type { GetDerivedStateFromError } from 'react';

import Button from 'Components/UI_Library/FormElements/Button';
import { randomAlphaNumeric } from 'Lib/randomAlphaNumeric';
import { logger } from 'utils/logger';

type ErrorBoundaryState = {
    errorReference: string | undefined;
};

type ErrorBoundaryProps = {
    children: React.ReactNode;
};

// FIXME: At the time of writing React only supports error boundary as [class
// component](https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary:~:text=There%20is%20currently%20no%20way%20to%20write%20an%20error%20boundary%20as%20a%20function%20component.%20However%2C%20you%20don%E2%80%99t%20have%20to%20write%20the%20error%20boundary%20class%20yourself.%20For%20example%2C%20you%20can%20use%20react%2Derror%2Dboundary%20instead.).
// Hopefully they will implement it for functional components at some point, but I think it's been a long time coming.
export default class RootErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { errorReference: undefined };
    }

    static getDerivedStateFromError: GetDerivedStateFromError<ErrorBoundaryProps, ErrorBoundaryState> = () => ({
        errorReference: randomAlphaNumeric({ size: 16 }),
    });

    // eslint-disable-next-line react/no-arrow-function-lifecycle -- It's a hassle typing a function and performance is not an issue here.
    componentDidCatch: React.Component['componentDidCatch'] = (
        error: Partial<Error> | undefined, // React expects error but anything can be thrown so preventing read properties on undefined runtime error here
        errorInfo
    ) => {
        logger.error(
            'RootErrorBoundary', // NOTE: This might be used in datadog filters so don't change it without updating those filters
            {
                error,
                errorDetails: { name: error?.name, message: error?.message, stack: error?.stack }, // The plain error isn't propagated to datadog it seems.
                errorReference: this.state.errorReference,
                errorInfo,
            }
        );
    };

    render() {
        if (this.state.errorReference) {
            // You can render any custom fallback UI
            return (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <img src="/img/Error.svg" alt="" />
                        <h1>Oops! Something went wrong...</h1>
                        <div style={{ textAlign: 'center' }}>
                            <p>
                                If this keeps happening, please contact support at{' '}
                                <a
                                    href={encodeURI(
                                        `mailto:support@paytron.com?subject=[${this.state.errorReference}] <Issue Summary>`
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    support@paytron.com
                                </a>{' '}
                                with the following reference:
                            </p>
                            {this.state.errorReference}
                        </div>
                        <br />
                        <Button href="/" onClickSideEffect={() => this.setState({ errorReference: undefined })}>
                            Take me back to safety
                        </Button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
